import React, { useState, useEffect, useCallback } from "react";
import { Box, Grid2, Stack, TextField, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { getAllProject, getProjectDailyReport } from "../../services/reports.service";
import MasterCommonTable from "../../sharedComponents/MaterCommonTable/MasterCommonTable";
import { Button, DatePicker } from "antd";
import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../context/AuthProvider";
import { useNavigate } from "react-router-dom";
import { getTokenbyData } from "../../services/user.service";

const ProjectDailyReport = () => {
    // Set default date to today
    const getDefaultDate = () => {
        return dayjs();
    };

    const [selectedDate, setSelectedDate] = useState(getDefaultDate());
    const [data, setData] = useState([]);
    const [selectedProject, setSelectedProject] = useState("");
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [projectDatas, setProjectDatas] = useState([]);
    const [totalHours, setTotalHours] = useState("");
    const [isOverAllAdmin, setIsOverAllAdmin] = useState(false);
    const navigate = useNavigate();
    const [isManager, setisManager] = useState(false);

    const columns = [
        { id: "projectName", label: "Project Name" },
        { id: "userName", label: "Name" },
        { id: "issueType", label: "Issue Type" },
        { id: "taskNo", label: "Task No" },
        { id: "workingHours", label: "Working Hours" },
        { id: "totalHours", label: "Total Hours" },
    ];

    const tableConfig = {
        isAction: false,
        isView: true,
    };

    const fetchTasks = async () => {
        try {
            const res = await getTokenbyData();
            const { isOverAllAdmin, isManager } = res.data || {};
            setIsOverAllAdmin(isOverAllAdmin);
            setisManager(isManager);
        } catch (error) {
            console.error("Error fetching tasks:", error);
        }
    };

    useEffect(() => {
        fetchTasks();
    }, []);

    const { accessToken } = useAuth();
    const getUserIdFromToken = () => {
        if (!accessToken) return null;
        try {
            const decoded = jwtDecode(accessToken);
            return decoded.id;
        } catch (error) {
            console.error("Invalid token", error);
            return null;
        }
    };

    const userId = getUserIdFromToken();

    const calculateDurationInHHMM = useCallback((workingHours) => {
        if (!workingHours || !workingHours.includes(" - ")) {
            return "00:00";
        }

        const [startTimeStr, endTimeStr] = workingHours.split(" - ");

        const parseTime = (timeStr) => {
            const trimmedTimeStr = timeStr.trim();
            const match = trimmedTimeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
            if (!match) return NaN;

            let [_, hours, minutes, modifier] = match;
            hours = parseInt(hours, 10);
            minutes = parseInt(minutes, 10);

            if (modifier) {
                modifier = modifier.toUpperCase();
                if (modifier === "PM" && hours < 12) {
                    hours += 12;
                }
                if (modifier === "AM" && hours === 12) {
                    hours = 0;
                }
            }
            return hours * 60 + minutes;
        };

        const startMinutes = parseTime(startTimeStr);
        const endMinutes = parseTime(endTimeStr);

        if (isNaN(startMinutes) || isNaN(endMinutes)) {
            return "00:00";
        }

        let durationMinutes = endMinutes - startMinutes;

        if (durationMinutes < 0) {
            durationMinutes = 0;
        }

        const hours = Math.floor(durationMinutes / 60);
        const minutes = durationMinutes % 60;

        const pad = (num) => num.toString().padStart(2, "0");

        return `${pad(hours)}:${pad(minutes)}`;
    }, []);

    const fetchReportData = useCallback(async () => {
        const dateToUse = !selectedDate ? getDefaultDate() : selectedDate;

        try {
            const formattedDate = dayjs(dateToUse).format("YYYY-MM-DD");

            const res = await getProjectDailyReport({
                date: formattedDate,
                projectId: selectedProjectId || "All",
            });

            if (res?.data) {
                const processedData = res.data.map((item) => ({
                    ...item,
                    totalHours: calculateDurationInHHMM(item.workingHours),
                }));
                setData(processedData);

                // Calculate total hours from the processed data
                const totalMinutes = processedData.reduce((sum, item) => {
                    if (item.totalHours) {
                        const [hours, minutes] = item.totalHours.split(":").map(Number);
                        return sum + hours * 60 + minutes;
                    }
                    return sum;
                }, 0);

                const totalH = Math.floor(totalMinutes / 60);
                const totalM = totalMinutes % 60;
                const pad = (num) => num.toString().padStart(2, "0");

                setTotalHours(`${pad(totalH)}:${pad(totalM)}`);
            }
        } catch (error) {
            console.log("Error in fetching report", error);
        }
    }, [
        selectedDate,
        selectedProjectId,
        calculateDurationInHHMM,
    ]);

    const handleProjectChange = (event, value) => {
        if (value?.label === "All") {
            setSelectedProject("All");
            setSelectedProjectId("All");
        } else {
            setSelectedProject(value?.title || value?.label);
            setSelectedProjectId(value?._id);
        }
    };

    const handleDateChange = (date) => {
        if (date) {
            // Valid date selected
            setSelectedDate(date);
        } else {
            // Date cleared, reset to today
            setSelectedDate(getDefaultDate());
        }
    };

    // Fetch data when filters change
    useEffect(() => {
        fetchReportData();
    }, [fetchReportData]);

    // Initialize with default values when data loads
    useEffect(() => {
        if (projectDatas.length > 0 && !selectedProject) {
            setSelectedProject("All");
            setSelectedProjectId("All");
        }
    }, [projectDatas, selectedProject]);

    useEffect(() => {
        const fetchProject = async () => {
            try {
                const res = await getAllProject();
                if (res?.data) {
                    const configData = res.data.map((e) => ({
                        ...e,
                        label: e.name,
                        title: e.name,
                    }));
                    setProjectDatas(configData);
                }
            } catch (error) {
                console.error("Error fetching projects:", error);
            }
        };

        fetchProject();
    }, []);

    const handleView = (task) => {
        navigate(`/home/taskDetails?taskId=${task._id}`, {
            state: {
                isOverAllAdmin: isOverAllAdmin,
                isManager: isManager
            },
        });
    };

    return (
        <div className="app-container">
            <Box>
                <Grid2 container spacing={4} mb={1} sx={{ width: "100%" }}>
                    <Grid2 size={{ xs: 3 }}>
                        <DatePicker
                            value={selectedDate}
                            onChange={handleDateChange}
                            className="datePicker w-100"
                            format={"DD-MM-YYYY"}
                            allowClear={true}
                            placeholder="Select Date"
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 3 }}>
                        <Autocomplete
                            value={selectedProject ? { label: selectedProject } : null}
                            options={[...projectDatas]}
                            getOptionLabel={(option) => option.label || option}
                            onChange={handleProjectChange}
                            placeholder="Select Project"
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Select Project"
                                    className="AutoComplete_InputBox"
                                    size="small"
                                />
                            )}
                            sx={{ width: "100%" }}
                        />
                    </Grid2>

                    {/* Total Hours Box on the right side */}
                    <Grid2 size={{ xs: 6 }} display="flex" justifyContent="flex-end" alignItems="center">
                        {totalHours && (
                            <Box
                                sx={{
                                    bgcolor: "#008080",
                                    color: "white",
                                    padding: "12px 24px",
                                    borderRadius: 2,
                                    boxShadow: "0 4px 12px rgba(0, 128, 128, 0.25)",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    Total Hours:
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                    {totalHours}
                                </Typography>
                            </Box>
                        )}
                    </Grid2>
                </Grid2>

                <MasterCommonTable
                    tableConfig={tableConfig}
                    data={data}
                    columns={columns}
                    tableTotalCount={data.length}
                    handleView={handleView}
                />
            </Box>
        </div>
    );
};

export default ProjectDailyReport;
